import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  addNotification,
  clearGlobalError,
  clearNotifications,
  removeNotification,
  resetUI,
  setCartOpen,
  setFiltersOpen,
  setGlobalError,
  setGlobalLoading,
  setLayout,
  setMobileMenuOpen,
  setSearchOpen,
  setSearchQuery,
  setShowFooter,
  setShowHeader,
  toggleCart,
  toggleFilters,
  toggleMobileMenu,
  toggleSearch,
} from '../store/slices/uiSlice'
import { AppDispatch, RootState } from '../store/store'

export const useUIRedux = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    showHeader,
    showFooter,
    globalLoading,
    globalError,
    isSearchOpen,
    searchQuery,
    isCartOpen,
    isMobileMenuOpen,
    isFiltersOpen,
    notifications,
  } = useSelector((state: RootState) => state.ui)

  // Layout actions
  const setHeaderVisibility = useCallback(
    (show: boolean) => {
      dispatch(setShowHeader(show))
    },
    [dispatch],
  )

  const setFooterVisibility = useCallback(
    (show: boolean) => {
      dispatch(setShowFooter(show))
    },
    [dispatch],
  )

  const setLayoutVisibility = useCallback(
    (showHeader: boolean, showFooter: boolean) => {
      dispatch(setLayout({ showHeader, showFooter }))
    },
    [dispatch],
  )

  // Loading actions
  const setLoading = useCallback(
    (loading: boolean) => {
      dispatch(setGlobalLoading(loading))
    },
    [dispatch],
  )

  // Error actions
  const setError = useCallback(
    (error: string | null) => {
      dispatch(setGlobalError(error))
    },
    [dispatch],
  )

  const clearError = useCallback(() => {
    dispatch(clearGlobalError())
  }, [dispatch])

  // Search actions
  const openSearch = useCallback(() => {
    dispatch(setSearchOpen(true))
  }, [dispatch])

  const closeSearch = useCallback(() => {
    dispatch(setSearchOpen(false))
  }, [dispatch])

  const setQuery = useCallback(
    (query: string) => {
      dispatch(setSearchQuery(query))
    },
    [dispatch],
  )

  const toggleSearchState = useCallback(() => {
    dispatch(toggleSearch())
  }, [dispatch])

  // Cart actions
  const openCart = useCallback(() => {
    dispatch(setCartOpen(true))
  }, [dispatch])

  const closeCart = useCallback(() => {
    dispatch(setCartOpen(false))
  }, [dispatch])

  const toggleCartState = useCallback(() => {
    dispatch(toggleCart())
  }, [dispatch])

  // Mobile menu actions
  const openMobileMenu = useCallback(() => {
    dispatch(setMobileMenuOpen(true))
  }, [dispatch])

  const closeMobileMenu = useCallback(() => {
    dispatch(setMobileMenuOpen(false))
  }, [dispatch])

  const toggleMobileMenuState = useCallback(() => {
    dispatch(toggleMobileMenu())
  }, [dispatch])

  // Filters actions
  const openFilters = useCallback(() => {
    dispatch(setFiltersOpen(true))
  }, [dispatch])

  const closeFilters = useCallback(() => {
    dispatch(setFiltersOpen(false))
  }, [dispatch])

  const toggleFiltersState = useCallback(() => {
    dispatch(toggleFilters())
  }, [dispatch])

  // Notification actions
  const showNotification = useCallback(
    (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
      dispatch(addNotification({ type, message }))
    },
    [dispatch],
  )

  const hideNotification = useCallback(
    (id: string) => {
      dispatch(removeNotification(id))
    },
    [dispatch],
  )

  const clearAllNotifications = useCallback(() => {
    dispatch(clearNotifications())
  }, [dispatch])

  // Reset UI
  const resetUIState = useCallback(() => {
    dispatch(resetUI())
  }, [dispatch])

  return {
    // State
    showHeader,
    showFooter,
    globalLoading,
    globalError,
    isSearchOpen,
    searchQuery,
    isCartOpen,
    isMobileMenuOpen,
    isFiltersOpen,
    notifications,

    // Layout actions
    setShowHeader: setHeaderVisibility,
    setShowFooter: setFooterVisibility,
    setLayout: setLayoutVisibility,

    // Loading actions
    setGlobalLoading: setLoading,

    // Error actions
    setGlobalError: setError,
    clearGlobalError: clearError,

    // Search actions
    setSearchOpen: openSearch,
    closeSearch,
    setSearchQuery: setQuery,
    toggleSearch: toggleSearchState,

    // Cart actions
    setCartOpen: openCart,
    closeCart,
    toggleCart: toggleCartState,

    // Mobile menu actions
    setMobileMenuOpen: openMobileMenu,
    closeMobileMenu,
    toggleMobileMenu: toggleMobileMenuState,

    // Filters actions
    setFiltersOpen: openFilters,
    closeFilters,
    toggleFilters: toggleFiltersState,

    // Notification actions
    addNotification: showNotification,
    removeNotification: hideNotification,
    clearNotifications: clearAllNotifications,

    // Reset
    resetUI: resetUIState,
  }
}
